import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchRecordingsAsync,
  setFilters,
  setPage,
  Recording,
  RecordingStatus
} from '../store/slices/recordingsSlice';
import LoadingSpinner from './LoadingSpinner';
import './Recordings.css';

const STATUS_OPTIONS: Array<{ value: 'all' | RecordingStatus; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' }
];

function fullName(p?: { firstName?: string | null; lastName?: string | null } | null): string {
  if (!p) return '';
  return [p.firstName, p.lastName].filter(Boolean).join(' ');
}

const Recordings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { recordings, loading, error, pagination, filters } = useAppSelector((state) => state.recordings);

  const reload = useCallback((page = pagination.currentPage) => {
    dispatch(fetchRecordingsAsync({
      page,
      limit: pagination.itemsPerPage,
      status: filters.status,
      unattributed: filters.unattributed,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      search: filters.search
    }));
  }, [dispatch, pagination.currentPage, pagination.itemsPerPage, filters]);

  useEffect(() => { reload(1); }, [reload]);

  return (
    <div className="recordings">
      <div className="recordings__header">
        <h1>Recordings</h1>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search consultation name…"
          value={filters.search}
          onChange={(e) => dispatch(setFilters({ search: e.target.value }))}
          className="search-input"
        />
        <select
          value={filters.status}
          onChange={(e) => dispatch(setFilters({ status: e.target.value }))}
          className="form-select"
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label className="filter-check">
          <input
            type="checkbox"
            checked={filters.unattributed}
            onChange={(e) => dispatch(setFilters({ unattributed: e.target.checked }))}
          />
          Unattributed only
        </label>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => dispatch(setFilters({ dateFrom: e.target.value }))}
          className="form-input"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => dispatch(setFilters({ dateTo: e.target.value }))}
          className="form-input"
        />
      </div>

      {error && <div className="recordings__error">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <table className="recordings__table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Consultation</th>
              <th>Student</th>
              <th>Duration</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {recordings.length === 0 && (
              <tr><td colSpan={6} className="recordings__empty">No recordings.</td></tr>
            )}
            {recordings.map((r: Recording) => (
              <tr key={r.id}>
                <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                <td>
                  <Link to={`/consultations/edit/${r.consultationId}`}>
                    {fullName(r.consultation) || `Consultation #${r.consultationId}`}
                  </Link>
                </td>
                <td>
                  {r.student ? (
                    <Link to={`/students/edit/${r.student.id}`}>{fullName(r.student) || `Student #${r.student.id}`}</Link>
                  ) : '—'}
                </td>
                <td>{r.durationSeconds != null ? `${Math.floor(r.durationSeconds / 60)}m ${r.durationSeconds % 60}s` : '—'}</td>
                <td>
                  <span className={`recordings__pill recordings__pill--${r.status}`}>{r.status}</span>
                </td>
                <td>
                  <Link to={`/recordings/${r.id}`} className="btn-secondary">Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pagination.totalPages > 1 && (
        <div className="recordings__pagination">
          <button
            disabled={pagination.currentPage <= 1}
            onClick={() => {
              const newPage = pagination.currentPage - 1;
              dispatch(setPage(newPage));
              reload(newPage);
            }}
          >Prev</button>
          <span>Page {pagination.currentPage} / {pagination.totalPages}</span>
          <button
            disabled={pagination.currentPage >= pagination.totalPages}
            onClick={() => {
              const newPage = pagination.currentPage + 1;
              dispatch(setPage(newPage));
              reload(newPage);
            }}
          >Next</button>
        </div>
      )}
    </div>
  );
};

export default Recordings;
